import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { format } from 'date-fns';
import Api from '../../Services/Api';

import { PageHeader, RsocNavHeader } from './';
import MainLayoutDemand from '../MainLoyout/MainLayoutDemand';

/**
 * RSOC Rules Component - Displays media buying rules logs and execution history
 */
const RsocRules = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [ruleFilter, setRuleFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Rules management state
  const [rules, setRules] = useState([]);
  const [ruleSchema, setRuleSchema] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [ruleFormData, setRuleFormData] = useState({
    name: '',
    description: '',
    priority: 50,
    enabled: true,
    conditions: [{ metric: '', operator: '', value: '', value1: '', value2: '' }],
    actions: [{ type: '', params: {} }]
  });

  // Fetch rules logs data
  const fetchRulesData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        ...(ruleFilter && { rule: ruleFilter }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo })
      };

      const response = await Api.getRulesLogs(params);
      
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError('Failed to load rules data');
      }
    } catch (err) {
      console.error('Error fetching rules data:', err);
      setError('Error loading rules data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch rules for management
  const fetchRules = async () => {
    setLoading(true);
    try {
      const [rulesResponse, schemaResponse] = await Promise.all([
        Api.getRules(),
        Api.getRuleSchema()
      ]);
      
      if (rulesResponse.data.success) {
        setRules(rulesResponse.data.data);
      }
      if (schemaResponse.data.success) {
        setRuleSchema(schemaResponse.data.data);
      }
    } catch (err) {
      console.error('Error fetching rules:', err);
      setError('Error loading rules: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle rule toggle
  const handleToggleRule = async (ruleId) => {
    try {
      const response = await Api.toggleRule(ruleId);
      if (response.data.success) {
        fetchRules();
      }
    } catch (err) {
      console.error('Error toggling rule:', err);
      setError('Error toggling rule: ' + (err.response?.data?.error || err.message));
    }
  };

  // Handle rule delete
  const handleDeleteRule = async (ruleId) => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      try {
        const response = await Api.deleteRule(ruleId);
        if (response.data.success) {
          fetchRules();
        }
      } catch (err) {
        console.error('Error deleting rule:', err);
        setError('Error deleting rule: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  // Handle rule edit
  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setRuleFormData({
      name: rule.name,
      description: rule.description,
      priority: rule.priority,
      enabled: rule.enabled,
      conditions: rule.conditions.length > 0 ? rule.conditions : [{ metric: '', operator: '', value: '', value1: '', value2: '' }],
      actions: rule.actions.length > 0 ? rule.actions : [{ type: '', params: {} }]
    });
    setShowRuleDialog(true);
  };

  // Handle rule save
  const handleSaveRule = async () => {
    try {
      const response = editingRule 
        ? await Api.updateRule(editingRule.id, ruleFormData)
        : await Api.createRule(ruleFormData);
      
      if (response.data.success) {
        setShowRuleDialog(false);
        setEditingRule(null);
        fetchRules();
      }
    } catch (err) {
      console.error('Error saving rule:', err);
      setError('Error saving rule: ' + (err.response?.data?.error || err.message));
    }
  };

  // Handle add new rule
  const handleAddRule = () => {
    setEditingRule(null);
    setRuleFormData({
      name: '',
      description: '',
      priority: 50,
      enabled: true,
      conditions: [{ metric: '', operator: '', value: '', value1: '', value2: '' }],
      actions: [{ type: '', params: {} }]
    });
    setShowRuleDialog(true);
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchRulesData();
    } else if (activeTab === 1) {
      fetchRules();
    }
  }, [activeTab, page, rowsPerPage, ruleFilter, dateFrom, dateTo]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRuleFilterChange = (event) => {
    setRuleFilter(event.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setRuleFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const formatRuleName = (ruleName) => {
    return ruleName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getRuleChipColor = (ruleName) => {
    switch (ruleName) {
      case 'pause_worst_campaigns':
        return 'error';
      case 'mark_for_review':
        return 'warning';
      case 'investigate_high_cpc':
        return 'info';
      case 'scale_high_performers':
        return 'success';
      case 'no_conversions_check':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderSummaryCards = () => {
    if (!data?.summary) return null;

    const cards = [
      { title: 'Total Executions', value: data.summary.total_executions, color: 'primary' },
      { title: 'Campaigns Paused', value: data.summary.total_paused, color: 'error' },
      { title: 'Pending Reviews', value: data.summary.pending_reviews, color: 'warning' },
      { title: 'Pending Optimizations', value: data.summary.pending_optimizations, color: 'info' },
      { title: 'Budget Changes Today', value: data.summary.budget_changes_today, color: 'success' },
      { title: 'Rules Executed Today', value: data.summary.rules_executed_today, color: 'secondary' }
    ];

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={2} key={index}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color={card.color + '.main'} fontWeight="bold">
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderFilters = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>Filters</Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Rule</InputLabel>
            <Select
              value={ruleFilter}
              onChange={handleRuleFilterChange}
              label="Rule"
            >
              <MenuItem value="">All Rules</MenuItem>
              {data?.available_rules?.map((rule) => (
                <MenuItem key={rule} value={rule}>
                  {formatRuleName(rule)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Date From"
            type="date"
            size="small"
            fullWidth
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Date To"
            type="date"
            size="small"
            fullWidth
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button variant="outlined" onClick={clearFilters} fullWidth>
            Clear Filters
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderRuleExecutionsTable = () => {
    if (!data?.rule_executions?.data) return null;

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Rule</TableCell>
              <TableCell>Campaign</TableCell>
              <TableCell>Metrics</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rule_executions.data.map((execution, index) => (
              <TableRow key={index}>
                <TableCell>
                  {format(new Date(execution.created_at), 'MMM dd, HH:mm')}
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatRuleName(execution.rule_name)}
                    color={getRuleChipColor(execution.rule_name)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap title={execution.campaign_name}>
                    {execution.campaign_name.length > 30 
                      ? execution.campaign_name.substring(0, 30) + '...'
                      : execution.campaign_name
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {execution.campaign_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  {execution.metrics && (
                    <Stack spacing={0.5}>
                      {JSON.parse(execution.metrics).roas && (
                        <Typography variant="caption">
                          ROAS: {JSON.parse(execution.metrics).roas}
                        </Typography>
                      )}
                      {JSON.parse(execution.metrics).spend && (
                        <Typography variant="caption">
                          Spend: ${JSON.parse(execution.metrics).spend}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </TableCell>
                <TableCell>
                  {execution.actions_taken && JSON.parse(execution.actions_taken).map((action, idx) => (
                    <Chip
                      key={idx}
                      label={action.type}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data.rule_executions.total || 0}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    );
  };

  const renderAdditionalLogs = () => (
    <Box sx={{ mt: 3 }}>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Paused Campaigns ({data?.campaign_logs?.length || 0})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Paused At</TableCell>
                  <TableCell>Log Only</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.campaign_logs?.slice(0, 10).map((campaign, index) => (
                  <TableRow key={index}>
                    <TableCell>{campaign.campaign_name}</TableCell>
                    <TableCell>
                      <Chip label={campaign.status} color={campaign.status === 'paused' ? 'error' : 'warning'} size="small" />
                    </TableCell>
                    <TableCell>{format(new Date(campaign.paused_at), 'MMM dd, HH:mm')}</TableCell>
                    <TableCell>{campaign.log_only ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Pending Reviews ({data?.review_campaigns?.length || 0})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.review_campaigns?.slice(0, 10).map((campaign, index) => (
                  <TableRow key={index}>
                    <TableCell>{campaign.campaign_name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={campaign.priority} 
                        color={campaign.priority === 'high' ? 'error' : campaign.priority === 'medium' ? 'warning' : 'default'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{campaign.issue || 'General review'}</TableCell>
                    <TableCell>{format(new Date(campaign.created_at), 'MMM dd, HH:mm')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const renderRulesManagement = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Rules Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddRule}
        >
          Add New Rule
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Rule Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Conditions</TableCell>
              <TableCell>Actions</TableCell>
              <TableCell>Control</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule) => (
              <TableRow key={rule.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {formatRuleName(rule.name)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {rule.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={rule.priority} 
                    color={rule.priority >= 90 ? 'error' : rule.priority >= 70 ? 'warning' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={rule.enabled ? 'Enabled' : 'Disabled'}
                    color={rule.enabled ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    {rule.conditions.slice(0, 2).map((condition, idx) => (
                      <Typography key={idx} variant="caption" color="text.secondary">
                        {condition.metric} {condition.operator} {condition.value || `${condition.value1}-${condition.value2}`}
                      </Typography>
                    ))}
                    {rule.conditions.length > 2 && (
                      <Typography variant="caption" color="text.secondary">
                        +{rule.conditions.length - 2} more
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {rule.actions.slice(0, 2).map((action, idx) => (
                      <Chip 
                        key={idx}
                        label={action.type} 
                        size="small"
                        variant="outlined"
                      />
                    ))}
                    {rule.actions.length > 2 && (
                      <Chip 
                        label={`+${rule.actions.length - 2}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={rule.enabled ? 'Disable' : 'Enable'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleRule(rule.id)}
                        color={rule.enabled ? 'success' : 'default'}
                      >
                        {rule.enabled ? <ToggleOnIcon /> : <ToggleOffIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditRule(rule)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRule(rule.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderRuleDialog = () => (
    <Dialog open={showRuleDialog} onClose={() => setShowRuleDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingRule ? 'Edit Rule' : 'Add New Rule'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Rule Name"
              value={ruleFormData.name}
              onChange={(e) => setRuleFormData({...ruleFormData, name: e.target.value})}
              required
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Priority"
              value={ruleFormData.priority}
              onChange={(e) => setRuleFormData({...ruleFormData, priority: parseInt(e.target.value)})}
              required
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={ruleFormData.enabled}
                  onChange={(e) => setRuleFormData({...ruleFormData, enabled: e.target.checked})}
                />
              }
              label="Enabled"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={ruleFormData.description}
              onChange={(e) => setRuleFormData({...ruleFormData, description: e.target.value})}
              required
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Conditions</Typography>
            {ruleFormData.conditions.map((condition, index) => (
              <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Metric</InputLabel>
                    <Select
                      value={condition.metric}
                      onChange={(e) => {
                        const newConditions = [...ruleFormData.conditions];
                        newConditions[index].metric = e.target.value;
                        setRuleFormData({...ruleFormData, conditions: newConditions});
                      }}
                    >
                      {ruleSchema && Object.entries(ruleSchema.metrics).map(([key, label]) => (
                        <MenuItem key={key} value={key}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={condition.operator}
                      onChange={(e) => {
                        const newConditions = [...ruleFormData.conditions];
                        newConditions[index].operator = e.target.value;
                        setRuleFormData({...ruleFormData, conditions: newConditions});
                      }}
                    >
                      {ruleSchema && Object.entries(ruleSchema.operators).map(([key, label]) => (
                        <MenuItem key={key} value={key}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {condition.operator === 'between' ? (
                  <>
                    <Grid item xs={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Value 1"
                        value={condition.value1}
                        onChange={(e) => {
                          const newConditions = [...ruleFormData.conditions];
                          newConditions[index].value1 = e.target.value;
                          setRuleFormData({...ruleFormData, conditions: newConditions});
                        }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Value 2"
                        value={condition.value2}
                        onChange={(e) => {
                          const newConditions = [...ruleFormData.conditions];
                          newConditions[index].value2 = e.target.value;
                          setRuleFormData({...ruleFormData, conditions: newConditions});
                        }}
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Value"
                      value={condition.value}
                      onChange={(e) => {
                        const newConditions = [...ruleFormData.conditions];
                        newConditions[index].value = e.target.value;
                        setRuleFormData({...ruleFormData, conditions: newConditions});
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const newConditions = ruleFormData.conditions.filter((_, i) => i !== index);
                      setRuleFormData({...ruleFormData, conditions: newConditions});
                    }}
                    disabled={ruleFormData.conditions.length === 1}
                  >
                    Remove
                  </Button>
                </Grid>
              </Grid>
            ))}
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const newConditions = [...ruleFormData.conditions, { metric: '', operator: '', value: '', value1: '', value2: '' }];
                setRuleFormData({...ruleFormData, conditions: newConditions});
              }}
            >
              Add Condition
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Actions</Typography>
            {ruleFormData.actions.map((action, index) => (
              <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Action Type</InputLabel>
                    <Select
                      value={action.type}
                      onChange={(e) => {
                        const newActions = [...ruleFormData.actions];
                        newActions[index].type = e.target.value;
                        setRuleFormData({...ruleFormData, actions: newActions});
                      }}
                    >
                      {ruleSchema && Object.entries(ruleSchema.action_types).map(([key, label]) => (
                        <MenuItem key={key} value={key}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Parameters (JSON)"
                    value={JSON.stringify(action.params)}
                    onChange={(e) => {
                      try {
                        const newActions = [...ruleFormData.actions];
                        newActions[index].params = JSON.parse(e.target.value);
                        setRuleFormData({...ruleFormData, actions: newActions});
                      } catch (err) {
                        // Invalid JSON, ignore
                      }
                    }}
                    placeholder="{}"
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const newActions = ruleFormData.actions.filter((_, i) => i !== index);
                      setRuleFormData({...ruleFormData, actions: newActions});
                    }}
                    disabled={ruleFormData.actions.length === 1}
                  >
                    Remove
                  </Button>
                </Grid>
              </Grid>
            ))}
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const newActions = [...ruleFormData.actions, { type: '', params: {} }];
                setRuleFormData({...ruleFormData, actions: newActions});
              }}
            >
              Add Action
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowRuleDialog(false)}>Cancel</Button>
        <Button onClick={handleSaveRule} variant="contained">
          {editingRule ? 'Update' : 'Create'} Rule
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <MainLayoutDemand>
      <Box sx={{ mb: 1.5 }}>
        <PageHeader 
          title="Media Buying Rules" 
          subtitle="Monitor automated rule executions and campaign actions"
        />
        <RsocNavHeader />
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Execution Logs" />
          <Tab label="Rules Management" />
        </Tabs>
      </Box>

      {activeTab === 0 && data && (
        <>
          {renderSummaryCards()}
          {renderFilters()}
          
          <Paper sx={{ mb: 2 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">Rule Execution History</Typography>
            </Box>
            {renderRuleExecutionsTable()}
          </Paper>

          {renderAdditionalLogs()}
        </>
      )}

      {activeTab === 1 && (
        <>
          {renderRulesManagement()}
          {renderRuleDialog()}
        </>
      )}
    </MainLayoutDemand>
  );
};

export default RsocRules;